"use client";

import { format } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar, type CalendarProps } from "./Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Label } from "./Label";
import { Button } from "./Button";
import { cn } from "@repo/shared/utils";
import { PopoverClose } from "@radix-ui/react-popover";
import { InputInfo } from "./InputInfo";
import { forwardRef, useState } from "react";

export interface DatePickerProps {
  id: string;
  label?: string;
  date: Date | undefined;
  className?: string;
  containerStyle?: string;
  onDateChange: (date: Date | undefined) => void;
  disabled?: boolean;
  calendarProps?: Omit<CalendarProps, "mode">;
  error?: boolean;
  errorMessage?: string;
  showIcon?: boolean;
  dateFormat?: string;
  placeholder?: React.ReactNode;
  closeOnSelect?: boolean;
}

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  function DatePicker(
    {
      id,
      label,
      date,
      className,
      containerStyle,
      onDateChange,
      disabled,
      calendarProps,
      error,
      errorMessage,
      showIcon = true,
      dateFormat = "PPP",
      placeholder = "Pick a date",
      closeOnSelect = true,
    }: DatePickerProps,
    ref
  ) {
    const [open, setOpen] = useState(false);

    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-1 mt-1.5", containerStyle)}
      >
        <Popover open={open} onOpenChange={setOpen}>
          {label && <Label label={label} id={id} />}
          <PopoverTrigger asChild>
            <Button
              disabled={disabled}
              variant="outline"
              className={cn(
                "justify-start text-left font-normal text-form-foreground overflow-hidden",
                !date && "text-muted-foreground",
                className
              )}
            >
              {showIcon && <CalendarIcon className="shrink-0 mr-2 h-4 w-4" />}
              {date ? format(date, dateFormat) : <span>{placeholder}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <div className="flex justify-between">
              <p className="py-2 px-5 -mb-1 text-sm font-medium">
                Pick a date:
              </p>
              <PopoverClose asChild>
                <Button type="button" variant="ghost" className="p-1 size-8">
                  <X className="size-4" />
                </Button>
              </PopoverClose>
            </div>
            <Calendar
              defaultMonth={date ?? new Date()}
              {...calendarProps}
              mode="single"
              selected={date}
              onSelect={(date) => {
                onDateChange(date);
                if (closeOnSelect) setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
        {error && errorMessage && <InputInfo info={errorMessage} />}
      </div>
    );
  }
);
